import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";
import type { ColumnFormatSpec, ConformanceContract, ExportFormatStructureNode } from "@capture/shared";

// ═══════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════

export interface ExportFormat {
  id: string;
  tenantId: string;
  name: string;
  formatType: string;
  config: Record<string, unknown>;
  columns: ExportColumn[];
  structure?: ExportFormatStructureNode[] | null;
  createdAt: string;
  updatedAt: string;
  // E-015 Phase 2 — Partner Profile metadata, all LIVE (2026-07-16):
  // fileNamingPattern = format-level filename default (destination template
  // overrides); schemaAttachment = imported-schema provenance; sampleData
  // feeds the layout preview.
  fileNamingPattern?: string | null;
  partnerNotes?: string | null;
  schemaAttachment?: unknown;
  sampleData?: unknown;
  /** R-186 — distinct JobTypes referencing this format (list endpoint only). */
  usedByJobTypeCount?: number;
  /** R-186 — destinations referencing this format (detail endpoint only). */
  destinations?: Array<{ id: string; name: string; jobTypeId: string }>;
  /** FR-001 — conformance contract produced by schema import. */
  conformanceContract?: ConformanceContract | null;
}

export interface ExportColumn {
  name: string;
  order: number;
  position?: number;
  length?: number;
  dataType?: string;
  /** EX-2/F-053 — typed format spec; optional so legacy API columns still typecheck. */
  format?: ColumnFormatSpec;
  /** EX-2/F-053 — schema note / receiver semantics. */
  note?: string;
  /** SP5 Task 8 — transaction-grain computed column expression. */
  expression?: string;
}

// ═══════════════════════════════════════════════════════════════
// List hook
// ═══════════════════════════════════════════════════════════════

export function useExportFormats() {
  const [formats, setFormats] = useState<ExportFormat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.get<{ formats: ExportFormat[] }>("/export-formats");
      setFormats(data.formats);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { formats, isLoading, error, refresh: load };
}

// ═══════════════════════════════════════════════════════════════
// Detail hook
// ═══════════════════════════════════════════════════════════════

export function useExportFormat(id: string | undefined) {
  const [format, setFormat] = useState<ExportFormat | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // F-030 — exposed so callers (e.g. the detail page) can refresh after an
  // in-place save so edited columns render without a hard browser refresh.
  const refetch = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const fmt = await api.get<ExportFormat>(`/export-formats/${id}`);
      setFormat(fmt);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { format, isLoading, refetch };
}

// ═══════════════════════════════════════════════════════════════
// Mutations
// ═══════════════════════════════════════════════════════════════

export function useCreateExportFormat() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const create = async (data: {
    name: string;
    formatType: string;
    config: Record<string, unknown>;
    /** Pass `structure` instead (the API derives `columns` from it). Legacy callers may still pass `columns` directly. */
    columns?: ExportColumn[];
    structure?: ExportFormatStructureNode[] | null;
    /** FR-001 — conformance contract produced by schema import (null when none). */
    conformanceContract?: ConformanceContract | null;
    // Task 3 (Partner Profile Completion) — settable at create time.
    fileNamingPattern?: string | null;
    partnerNotes?: string | null;
    schemaAttachment?: unknown;
    sampleData?: unknown;
  }): Promise<ExportFormat> => {
    setIsSubmitting(true);
    try {
      return await api.post<ExportFormat>("/export-formats", data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { create, isSubmitting };
}

export interface ExportFormatPatch {
  name?: string;
  config?: Record<string, unknown>;
  columns?: ExportColumn[];
  structure?: ExportFormatStructureNode[] | null;
  /** Use 'settings' key for PATCH requests; server maps settings → config. */
  settings?: Record<string, unknown>;
  // E-015 Phase 2 — Partner Profile metadata. null clears the field.
  fileNamingPattern?: string | null;
  partnerNotes?: string | null;
  schemaAttachment?: unknown;
  sampleData?: unknown;
  /** FR-001 — conformance contract to persist; null clears it. */
  conformanceContract?: ConformanceContract | null;
}

export function useUpdateExportFormat(id: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** PUT — full replacement (name, config, columns). */
  const update = async (data: Partial<Pick<ExportFormat, "name" | "config" | "columns">>): Promise<ExportFormat> => {
    setIsSubmitting(true);
    try {
      return await api.put<ExportFormat>(`/export-formats/${id}`, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  /** PATCH — partial update (settings + columns only; formatType is server-rejected). */
  const patch = async (data: ExportFormatPatch): Promise<ExportFormat> => {
    setIsSubmitting(true);
    try {
      return await api.patch<ExportFormat>(`/export-formats/${id}`, data);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { update, patch, isSubmitting };
}

export function useDeleteExportFormat() {
  const remove = async (id: string): Promise<void> => {
    await api.delete(`/export-formats/${id}`);
  };
  return { remove };
}

/**
 * R-201 — clone an ExportFormat (variations workflow). Duplicates formatType +
 * config + columns under a new name (defaults server-side to "<name> (copy)").
 */
export function useCloneExportFormat() {
  const clone = async (id: string, name?: string): Promise<ExportFormat> => {
    return api.post<ExportFormat>(`/export-formats/${id}/clone`, name ? { name } : {});
  };
  return { clone };
}

